<template>
  <div class="card task-detail-point-section">
    <div class="card-header d-flex detail-point-header">
      <div class="d-flex gap-3">
        <div
          class="__index"
          :style="{
            backgroundColor: !['VISITING', 'VISITED'].includes(point?.status.raw_type)
              ? '#67718B'
              : point?.status.foreground_color,
          }"
        >
          <i
            v-if="!isDefaultType(point?.type)"
            class="mc-icon"
            :class="{
              'icon-fill_flag': point?.type === 'END',
              'icon-fill_home': point?.type === 'START',
            }"
          />
          <template v-else>
            {{ hasStartPoint ? selectedPointIdx : selectedPointIdx + 1 }}
          </template>
        </div>
        <div class="d-flex flex-wrap gap-2">
          <div class="__title">
            {{ point?.address.name }}
          </div>
          <div
            v-if="isLiveTrack"
            class="__status"
            :style="{
              backgroundColor: point?.status?.background_color,
              color: point?.status?.foreground_color,
            }"
          >
            {{ computedStatus(point) }}
          </div>
        </div>
      </div>
      <b-button class="__close-button" @click="closeModal">
        <i class="mc-icon icon-outline_close" />
      </b-button>
    </div>
    <div class="card-body detail-point-body">
      <div class="livetrack-thumbnail">
        <h5>{{ $t('fleetTaskInstant.title.locationDetails') }}</h5>
        <div v-if="isLiveTrack && isMobileView">
          {{ point?.address?.full_name }}
        </div>
      </div>

      <div class="detail-point-card">
        <div class="__header">
          <div>{{ $t('fleetTaskInstant.text.arrival') }}</div>
          <div
            v-if="isPointVisited(point?.status.raw_type) && point?.late_arrival_time"
            class="__status"
          >
            <span class="__status-dots"></span>
            <span class="__status-text">{{ formatLateValues(point?.late_arrival_time, false) }}</span>
          </div>
        </div>
        <div class="__body">
          <div class="__column">
            <div>Target</div>
            <div>
              {{
                point?.target_time_arrival
                  ? formatDateWithTz(point?.target_time_arrival, 'DD/MM/YYYY HH:mm')
                  : '-'
              }}
            </div>
          </div>
          <div class="__column">
            <div>{{ $t('fleetTaskInstant.text.actual') }}</div>
            <div>
              {{
                point?.actual_time_arrival
                  ? formatDateWithTz(point?.actual_time_arrival, 'DD/MM/YYYY HH:mm')
                  : '-'
              }}
            </div>
          </div>
        </div>
      </div>
      <div class="detail-point-card">
        <div class="__header">
          <div>{{ $t('fleetTaskInstant.text.activityDuration') }}</div>
          <div
            v-if="isPointVisited(point?.status.raw_type) && point?.late_service_time"
            class="__status"
          >
            <span class="__status-dots"></span>
            <span class="__status-text">{{ formatLateValues(point?.late_service_time, false) }}</span>
          </div>
        </div>
        <div class="__body">
          <div class="__column">
            <div>Target</div>
            <div>
              {{
                point?.service_time
                  ? formatDuration(point?.service_time)
                  : '-'
              }}
            </div>
          </div>
          <div class="__column">
            <div>{{ $t('fleetTaskInstant.text.actual') }}</div>
            <div>
              {{
                point?.actual_service_time
                  ? formatDuration(point?.actual_service_time)
                  : '-'
              }}
            </div>
          </div>
        </div>
      </div>
      <div class="detail-point-card">
        <div class="__header">
          <div>{{ $t('fleetTaskInstant.text.departure') }}</div>
          <div
            v-if="isPointVisited(point?.status?.raw_type) && point?.late_departure_time"
            class="__status"
          >
            <span class="__status-dots"></span>
            <span class="__status-text">{{ formatLateValues(point?.late_departure_time, false) }}</span>
          </div>
        </div>
        <div class="__body">
          <div class="__column">
            <div>Target</div>
            <div>
              {{
                point?.target_time_departure
                  ? formatDateWithTz(point?.target_time_departure, 'DD/MM/YYYY HH:mm')
                  : '-'
              }}
            </div>
          </div>
          <div class="__column">
            <div>{{ $t('fleetTaskInstant.text.actual') }}</div>
            <div>
              {{
                point?.actual_time_departure
                  ? formatDateWithTz(point?.actual_time_departure, 'DD/MM/YYYY HH:mm')
                  : '-'
              }}
            </div>
          </div>
        </div>
      </div>
      <div class="detail-point-note-wrapper">
        <div>{{ $t('fleetTaskInstant.text.locationNote') }}</div>
        <div>{{ point?.note ? point?.note : '-' }}</div>
      </div>
      <div class="detail-point-proof-wrapper">
        <div>
          <div>{{ $t('fleetTaskInstant.text.assignmentProof') }}</div>
          <div v-if="point?.proof">
            {{ $t('fleetTaskInstant.text.createdOn', { time: formatDateWithTz(point.proof?.created_on, 'DD MMM YYYY, HH:mm') ?? '-' }) }}
          </div>
          <div v-if="point?.proof">
            {{ $t('fleetTaskInstant.text.by', { by: point.proof?.created_by ?? '-' }) }}
          </div>
        </div>
        <div v-if="point?.attachment?.photos" class="proof-grid">
          <div v-for="(photo, idx) in point?.attachment?.photos" :key="photo.id" class="proof-wrapper">
            <img
              class="proof-image"
              :src="photo.file.url"
              loading="lazy"
              @click="handleOpenPreview(idx)"
            />
            <button
              class="__download-btn"
              @click="customHandlers?.handleDownloadImg(photo.file.url, `foto${idx + 1}.jpg`)"
            >
              <i class="icon-outline_download"></i>
            </button>
          </div>
        </div>
        <div v-else>
          -
        </div>
      </div>
    </div>
    <PreviewImage
      v-if="activeAttachmentIdx !== null"
      :attachments="point?.attachment?.photos"
      :activeAttachmentIdx="activeAttachmentIdx"
      :customHandlers="customHandlers"
      @update:activeAttachmentIdx="activeAttachmentIdx = $event"
      @handleDownloadImg="customHandlers?.handleDownloadImg"
    />
  </div>
</template>
<script>
import { mapGetters, mapState } from 'vuex'

import PreviewImage from '../../../components/PreviewImage'

import { formatDateWithTz } from '@/helpers/tzDate'
import { statusIdToLabelMap } from '../../../lib/constants/point-status'
import { formatLateValues } from '../../../lib/helpers/formatValues'
import { EventBus } from '../../../lib/event-bus'

import * as FleetTaskInstant from '@/services/rest-api/lib/fleet-task-instant/fleet-task-instant'

import moment from 'moment'
moment.locale('id')

export default {
  components: {
    PreviewImage,
  },
  inject: {
    isMobileView : {
      default: false
    }
  },
  props: {
    fleetTaskInstantId: {
      type: String,
      required: false,
      default: null,
    },
    isLiveTrack: {
      type: Boolean,
      default: false,
    }
  },
  data() {
    return {
      isLoading: false,
      activeAttachmentIdx: null,
      customHandlers: {
        handleDownloadImg: async (imgUrl, fileName) => {
          const res = await fetch(imgUrl, {
            headers: new Headers({
              'Cache-Control': 'no-cache',
            }),
          })
          if (!res) return

          const blob = await res.blob()
          const url = URL.createObjectURL(blob)
          Object.assign(document.createElement('a'), { href: url, download: fileName }).click()
          URL.revokeObjectURL(url)
        },

        handlePathUrl: (input) => {
          return input.file.url
        },
      },
      statusIdToLabelMap
    }
  },
  computed: {
    ...mapState('fleetTaskInstant', ['selectedPointIdx']),
    ...mapGetters({
      hasStartPoint: 'fleetTaskInstant/hasStartPoint',
      point: 'fleetTaskInstant/selectedPoint',
    }),
    computedStatus() {
      return (item) => {
        const mappedStatus = this.statusIdToLabelMap[item?.status?.raw_type]

        if (mappedStatus) {
          return this.$t(mappedStatus)
        }

        return item?.status?.name
      }
    },
  },
  created() {
    EventBus.$on('get-point-detail',this.getPointDetail)
  },
  beforeDestroy() {
    EventBus.$off('get-point-detail',this.getPointDetail)
  },
  methods: {
    formatLateValues,
    formatDateWithTz,

    formatDuration(durationISO8601) {
      const duration = moment.duration(durationISO8601)

      const days = duration.days()
      const hours = duration.hours()
      const minutes = duration.minutes()
      const seconds = duration.seconds()

      const formattedDuration = []

      if (days > 0) {
        const unit = this.$t('fleetTaskInstant.time.day')

        formattedDuration.push(`${days} ${unit}`)
      }

      if (hours > 0) {
        const unit = this.$t('fleetTaskInstant.time.hour')

        formattedDuration.push(`${hours} ${unit}`)
      }

      if (minutes > 0) {
        const unit = this.$t('fleetTaskInstant.time.minute')

        formattedDuration.push(`${minutes} ${unit}`)
      }

      if (seconds > 0) {
        const unit = this.$t('fleetTaskInstant.time.second')

        formattedDuration.push(`${seconds} ${unit}`)
      }

      return formattedDuration.join(' ')
    },

    async getPointDetail() {
      this.isLoading = true
      let proof = this.point?.proof
      let attachment = this.point?.attachment

      if (!this.isLiveTrack) {
        const res = await FleetTaskInstant.getPointDetail(this.fleetTaskInstantId, this.point.id)
        
        if (res?.errors || res?.data == null) {
          this.$mcToast.error('Terjadi kesalahan')
          this.isLoading = false
          return
        }

        proof = res.data.proof
        attachment = res.data.attachment
      }

      this.$set(this.point, 'proof', proof)
      this.$set(this.point, 'attachment', attachment)
      this.isLoading = false
    },

    isPointVisited(status) {
      return status === 'VISITED'
    },

    isDefaultType(value) {
      return 'DEFAULT' === value
    },

    async closeModal() {
      this.$store.dispatch('fleetTaskInstant/setSelectedPointIdx', null)

      if (this.isLiveTrack) {
        await this.$nextTick()
        EventBus.$emit('calculate-comp-height')
      } else {
        await this.$nextTick()
  
        EventBus.$emit('focus-point', false)
      }
    },

    handleOpenPreview(idx) {
      this.activeAttachmentIdx = idx
    },
  },
}
</script>
<style lang="scss">
@import 'style.scss';
</style>
